import { useTranslate } from "@tolgee/react";
import { Loader2 } from "lucide-react";
import { useState } from "react";
import { Button } from "#/components/ui/button.tsx";
import { CheckboxIndicator } from "#/components/ui/checkbox.tsx";
import {
	Dialog,
	DialogCloseButton,
	DialogContent,
	DialogTitle,
	DialogTrigger,
} from "#/components/ui/dialog.tsx";
import type { useUpdateLeverage } from "#/hooks/use-trading-actions";
import { cn } from "#/lib/utils";
import { getHyperliquidErrorMessage } from "../../lib/hyperliquid-error.ts";
import {
	type MarginMode,
	marginModeOptions,
} from "./constants/order-form.constants.ts";
import type { MarginCapability } from "./utils/margin-mode.ts";

type MarginModeDialogProps = {
	coin: string;
	value: MarginMode;
	onChange: (nextValue: MarginMode) => void;
	currentLeverage: number;
	hasAvailableFunds: boolean;
	updateLeverage: ReturnType<typeof useUpdateLeverage>;
	marginCapability: MarginCapability;
	triggerClassName?: string;
};

export function MarginModeDialog({
	coin,
	value,
	onChange,
	currentLeverage,
	hasAvailableFunds,
	updateLeverage,
	marginCapability,
	triggerClassName,
}: MarginModeDialogProps) {
	const { t } = useTranslate();
	const [open, setOpen] = useState(false);
	const [pendingValue, setPendingValue] = useState<MarginMode>(value);
	const selectedOption = marginModeOptions.find(
		(option) => option.value === value,
	);

	function handleOpenChange(nextOpen: boolean) {
		setOpen(nextOpen);
		if (nextOpen) {
			setPendingValue(value);
		}
	}

	async function handleConfirm() {
		if (updateLeverage.isPending) return;
		if (pendingValue === value) {
			setOpen(false);
			return;
		}

		try {
			await updateLeverage.mutateAsync({
				coin,
				isCross:
					marginCapability.supportsCrossMargin && pendingValue === "Cross",
				leverage: currentLeverage,
			});
			onChange(pendingValue);
			setOpen(false);
		} catch {
			// error is tracked in updateLeverage.error
		}
	}

	return (
		<Dialog open={open} onOpenChange={handleOpenChange}>
			<DialogTrigger asChild>
				<Button
					variant={triggerClassName ? "ghost" : "outline"}
					className={cn("flex-1", triggerClassName)}
				>
					{selectedOption ? t(selectedOption.labelKey) : value}
				</Button>
			</DialogTrigger>
			<DialogContent className="w-[min(360px,calc(100vw-2rem))] rounded-[18px] border border-trade-border-subtle bg-[linear-gradient(180deg,var(--trade-surface-overlay),var(--trade-surface-overlay-end))] p-5 shadow-[inset_0_0_0_1px_rgba(255,255,255,0.04),0_30px_60px_rgba(0,0,0,0.45)]">
				<div className="flex items-center justify-between gap-3">
					<DialogTitle className="text-body2-bold text-trade-text-strong">
						{t("margin.title", { coin })}
					</DialogTitle>
					<DialogCloseButton aria-label="Close margin mode dialog" />
				</div>

				<div className="mt-3 grid gap-3">
					{marginModeOptions.map((option) => {
						const isSelected = pendingValue === option.value;
						const isDisabled =
							option.value === "Cross" && !marginCapability.supportsCrossMargin;

						return (
							<button
								key={option.value}
								type="button"
								disabled={isDisabled}
								onClick={() => !isDisabled && setPendingValue(option.value)}
								className={cn(
									"flex w-full cursor-pointer flex-col gap-2.5 rounded-[10px] border border-trade-border-subtle bg-trade-surface p-3.5 text-left transition-[border-color,background-color,box-shadow] hover:bg-trade-interactive",
									isSelected &&
										"border-trade-accent bg-[linear-gradient(180deg,color-mix(in_srgb,var(--color-theme-primary-normal)_8%,transparent)_0%,color-mix(in_srgb,var(--color-theme-primary-normal)_3%,transparent)_100%)] shadow-[0_0_0_1px_color-mix(in_srgb,var(--color-theme-primary-normal)_14%,transparent)]",
									isDisabled && "cursor-not-allowed opacity-40",
								)}
							>
								<div className="flex items-center gap-2.5 text-body2-bold text-trade-text-strong">
									<CheckboxIndicator checked={isSelected} />
									<span>{t(option.labelKey)}</span>
								</div>
								<p className="text-label2 text-trade-text-secondary">
									{t(option.descriptionKey)}
								</p>
							</button>
						);
					})}
				</div>

				{marginCapability.crossDisabledReasonKey && (
					<p className="mt-3 text-label2 text-trade-text-secondary">
						{t(marginCapability.crossDisabledReasonKey)}
					</p>
				)}

				{hasAvailableFunds ? (
					<>
						{updateLeverage.error && (
							<p className="mt-2 text-label2 text-trade-red">
								{getHyperliquidErrorMessage(
									t,
									updateLeverage.error,
									t("toast.error-leverage-update"),
								)}
							</p>
						)}

						<Button
							disabled={updateLeverage.isPending}
							className="mt-3.5 min-h-11 w-full transition-[filter,transform] hover:brightness-105 disabled:opacity-60"
							onClick={handleConfirm}
						>
							{updateLeverage.isPending ? (
								<>
									<Loader2 className="h-4 w-4 animate-spin" />
									{t("actions.updating")}
								</>
							) : (
								t("actions.confirm")
							)}
						</Button>
					</>
				) : (
					<div className="mt-4 rounded-[10px] border border-trade-border-subtle bg-trade-surface-inset p-4 shadow-[inset_0_0_0_1px_rgba(255,255,255,0.02)]">
						<p className="text-label1 text-trade-text-secondary">
							{t("margin.needs-funds")}
						</p>
					</div>
				)}
			</DialogContent>
		</Dialog>
	);
}
